import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex, Icon, Text, TextInput } from '@televet/kibble-ui';
import { ChannelStatusAction, useMergeProfileClientSearchLazyQuery } from 'shared/types/graphql';
import useDebounce from 'shared/hooks/useDebounce';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import MergeProfilesClientSearchResult from './MergeProfilesClientSearchResult';
import { getClinicPetParentDisplayName } from 'shared/utils';
import useClinicUser from 'shared/hooks/useClinicUser';
import { getSearchResultMergeDisabledReason } from '../utils';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

interface IMergeProfilesClientSearchProps {
  clinicPetParentId: string;
  clinicPetParentPimsId: string | undefined;
  hasActiveConversation: boolean;
  selectedMergeProfile: string | undefined;
  setSelectedMergeProfile: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const MergeProfilesClientSearch = ({
  clinicPetParentId,
  clinicPetParentPimsId,
  hasActiveConversation,
  selectedMergeProfile,
  setSelectedMergeProfile,
}: IMergeProfilesClientSearchProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery] = useDebounce(searchTerm, 350);

  const [searchForClients, { data: searchResultData, loading: isSearching }] = useMergeProfileClientSearchLazyQuery();

  const { currentClinicId } = useClinicUser();
  const { primaryIntegrationName, isMergeExistingPimsProfileSupported } = useIntegrationsProvider();

  const onSearchInputChange = useCallback((queryText: string) => {
    setSearchTerm(queryText);
  }, []);

  useEffect(() => {
    if (!searchQuery || !currentClinicId) return;

    searchForClients({
      variables: {
        where: {
          fullName: searchQuery,
        },
        clinicId: currentClinicId,
        take: 20,
      },
    });
  }, [searchForClients, searchQuery, currentClinicId]);

  const searchResults = useMemo(
    () => searchResultData?.clinicPetParentSearch.filter((parent) => parent.id !== clinicPetParentId) || [],
    [clinicPetParentId, searchResultData?.clinicPetParentSearch],
  );

  const searchResultCount = useMemo(() => searchResults.length || 0, [searchResults.length]);

  return (
    <Flex direction="column" w="520px">
      <TextInput
        autoFocus
        leftElement={<Icon name="magnifyingGlass" />}
        rightElement={<Button iconName="close" variant="ghostNeutral" onClick={(): void => onSearchInputChange('')} />}
        placeholder="Search by client name"
        value={searchTerm}
        onChange={(e): void => onSearchInputChange(e.currentTarget.value)}
      />
      <Flex h="400px" mt={6}>
        {isSearching && (
          <Flex m="auto">
            <LoadingSpinner message="Searching for Clients" />
          </Flex>
        )}
        {!isSearching && !!searchQuery && (
          <Flex w="100%" direction="column">
            <Text>{`${searchResultCount} Client Profile Result${searchResultCount !== 1 ? 's' : ''}`}</Text>
            {!!searchResultCount && (
              <Flex
                direction="column"
                mt={2}
                border="1px"
                borderColor="border.default"
                borderRadius={8}
                p={1}
                gap={1}
                overflow="auto"
              >
                {searchResults.map((profile) => {
                  const name = getClinicPetParentDisplayName({
                    firstName: profile?.firstName,
                    lastName: profile?.lastName,
                  });
                  const petNames = profile.pets.map((pet) => pet.name);
                  const hasActiveChannelChannelMembers = !!profile.channelMembers.some(
                    (member) => member.channel?.channelStatus?.channelStatusAction === ChannelStatusAction.Active,
                  );
                  const disabledReason = getSearchResultMergeDisabledReason(
                    profile,
                    clinicPetParentPimsId,
                    !!isMergeExistingPimsProfileSupported,
                    primaryIntegrationName,
                    hasActiveConversation && hasActiveChannelChannelMembers,
                  );
                  return (
                    <MergeProfilesClientSearchResult
                      key={profile.id}
                      clientName={name}
                      clientId={profile.id}
                      disabledReason={disabledReason}
                      petNames={petNames}
                      selectedMergeProfile={selectedMergeProfile}
                      setSelectedMergeProfile={setSelectedMergeProfile}
                    />
                  );
                })}
                {searchResultCount === 20 && (
                  <Flex m={1}>
                    <Text size="sm" variant="subtle">
                      Please refine your search query to see more results.
                    </Text>
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default MergeProfilesClientSearch;
